import type { BlobId } from '~/types/ids'
import { newId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from './db'
import { type StorageError, toStorageError } from './errors'
import { notifyWritten } from './invalidation'
import { STORE } from './schema'

/**
 * Binary content store.
 *
 * **Blob payloads are immutable once written (D-029).** There is deliberately
 * no `updateBlob`: changing an image means writing a new blob and repointing
 * the owning record. Two things depend on that guarantee — the pre-migration
 * recovery snapshot can safely contain structured records only, and a migration
 * can never corrupt image bytes.
 */

export interface StoredBlob {
  readonly blobId: string
  readonly blob: Blob
  readonly mimeType: string
  readonly byteSize: number
}

export async function putBlob(
  db: SolaDatabase,
  blob: Blob,
  mimeType: string,
): Promise<Result<BlobId, StorageError>> {
  try {
    const blobId = newId<'BlobId'>()
    await db.put(STORE.blobs, { blobId, blob, mimeType, byteSize: blob.size })
    notifyWritten(STORE.blobs)
    return ok(blobId)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function getBlob(
  db: SolaDatabase,
  blobId: BlobId,
): Promise<Result<StoredBlob | undefined, StorageError>> {
  try {
    return ok((await db.get(STORE.blobs, blobId)) as StoredBlob | undefined)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function deleteBlob(
  db: SolaDatabase,
  blobId: BlobId,
): Promise<Result<void, StorageError>> {
  try {
    await db.delete(STORE.blobs, blobId)
    notifyWritten(STORE.blobs)
    return ok(undefined)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function listBlobs(
  db: SolaDatabase,
): Promise<Result<readonly StoredBlob[], StorageError>> {
  try {
    return ok((await db.getAll(STORE.blobs)) as readonly StoredBlob[])
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function totalBlobBytes(db: SolaDatabase): Promise<Result<number, StorageError>> {
  const all = await listBlobs(db)
  if (!all.ok) return all
  return ok(all.value.reduce((sum, entry) => sum + (entry.byteSize ?? entry.blob.size), 0))
}

/**
 * Every blob id referenced by a record that owns binary content.
 *
 * Used by the orphan sweep and by restore validation, which requires every
 * referenced blob to be present before it will mutate anything.
 *
 * **A record may own more than one blob, and reading only `blobId` is a bug
 * that destroys data silently.** A photo owns its stored image *and* its
 * derived thumbnail (D-081). A sweep that collected only the first would find
 * every thumbnail unreferenced and delete it, leaving a grid of broken images
 * with no error anywhere — the orphan sweep is a backstop, so nothing would
 * report that it had just eaten half the library. The reference fields are
 * therefore listed explicitly and read from every record.
 */
const BLOB_REFERENCE_FIELDS = ['blobId', 'thumbnailBlobId'] as const

export async function referencedBlobIds(
  db: SolaDatabase,
): Promise<Result<ReadonlySet<string>, StorageError>> {
  try {
    const referenced = new Set<string>()
    for (const store of [STORE.photos, STORE.attachments] as const) {
      for (const record of await db.getAll(store)) {
        for (const field of BLOB_REFERENCE_FIELDS) {
          const blobId = (record as Record<string, unknown>)[field]
          if (typeof blobId === 'string') referenced.add(blobId)
        }
      }
    }
    return ok(referenced)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Remove blobs no record points at.
 *
 * A backstop only. Deletion of an owning record removes its blob in the same
 * transaction, so an orphan means something already went wrong.
 */
export async function sweepOrphanBlobs(db: SolaDatabase): Promise<Result<number, StorageError>> {
  const referenced = await referencedBlobIds(db)
  if (!referenced.ok) return referenced

  const all = await listBlobs(db)
  if (!all.ok) return all

  const orphans = all.value.filter((entry) => !referenced.value.has(entry.blobId))
  try {
    for (const orphan of orphans) {
      await db.delete(STORE.blobs, orphan.blobId)
    }
    if (orphans.length > 0) notifyWritten(STORE.blobs)
    return ok(orphans.length)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
